import { useMemo } from 'react';
import { useCatalog, useShelf } from '@/pages/roles/stock-manager/lib/useStock';

export interface StockRow {
  id: string;
  name: string;
  category: string | null;
  unit: string | null;
  inStock: number;
  perMonth: number | null;
  /** Per unit, in major units. Null when nothing priced it yet. */
  unitCost: number | null;
  value: number | null;
  burn: number | null;
  low: boolean;
  expiringSoon: boolean;
  nextExpiry: string | null;
  lots: number;
}

/**
 * The shelf, priced. Shared with the Reports home so the door's headline is
 * the figure this report prints — same query keys, same cached answer.
 */
export function useStockRows(): { rows: StockRow[]; loading: boolean } {
  const shelf = useShelf();
  const catalog = useCatalog();
  const rows = useMemo<StockRow[]>(() => {
    const cost = new Map(
      (catalog.data ?? []).map((c) => [c.id, c.lastCostMinor ?? c.standardCostMinor ?? null]),
    );
    const items = shelf.data ?? [];
    return items
      .map((it) => {
        const minor = cost.get(it.id) ?? null;
        const unitCost = minor === null ? null : minor / 100;
        return {
          id: it.id,
          name: it.name,
          category: it.category,
          unit: it.unit,
          inStock: it.inStock,
          perMonth: it.perMonth,
          unitCost,
          value: unitCost === null ? null : unitCost * it.inStock,
          burn: unitCost === null || it.perMonth === null ? null : unitCost * it.perMonth,
          low: it.low,
          expiringSoon: it.expiringSoon,
          nextExpiry: it.nextExpiry,
          lots: it.lots.length,
        };
      })
      .sort((a, b) => (b.burn ?? -1) - (a.burn ?? -1) || (b.perMonth ?? -1) - (a.perMonth ?? -1));
  }, [shelf.data, catalog.data]);

  return { rows, loading: shelf.isLoading };
}

export function stockTotals(rows: StockRow[]) {
  return {
    totalValue: rows.reduce((s, r) => s + (r.value ?? 0), 0),
    totalBurn: rows.reduce((s, r) => s + (r.burn ?? 0), 0),
    expiring: rows.filter((r) => r.expiringSoon).length,
    top: rows.find((r) => (r.burn ?? 0) > 0) ?? null,
  };
}

